import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import axios from 'axios';

import Button from 'components/Button';
import Input from 'components/Input';
import { fetchCar, inputChange, setOemDetails } from 'ducks/valuation';
import regex from 'utils/regex';
import { getApiUrl } from 'utils/getApiUrl';
import { sendSelectMakeEvent, sendSelectModelEvent } from 'utils/ga/events';
import FAQs from '../FAQs/FAQs';
import { faqContentCol1 } from '../FAQs/faqContent';
import style from './style.scss';

export const intentOptions = {
  SELLING_BUYING_NEW: 'selling-buying-new',
  SELLING_BUYING_USED: 'selling-buying-used',
  BUYING: 'buying',
  INSURING: 'insuring'
};

export class EnterCarDetailsForm extends React.Component {
  static propTypes = {
    fetchCar: PropTypes.func.isRequired,
    formValues: PropTypes.shape({
      intent: PropTypes.string,
      mileage: PropTypes.string,
      vrm: PropTypes.string,
      desiredMake: PropTypes.string,
      desiredModel: PropTypes.string
    }),
    history: PropTypes.shape({}).isRequired,
    inputChange: PropTypes.func.isRequired,
    setOemDetails: PropTypes.func.isRequired,
    isVrmValid: PropTypes.bool
  };

  static defaultProps = {};

  constructor(props) {
    super(props);
    this.radioButtonsList = [
      {
        id: intentOptions.SELLING_BUYING_NEW,
        label: 'Selling, and buying a brand new car'
      },
      {
        id: intentOptions.SELLING_BUYING_USED,
        label: 'Selling, and buying a used car'
      },
      { id: intentOptions.BUYING, label: 'Buying this car' },
      { id: intentOptions.INSURING, label: 'Insuring this car' }
    ];
    this.state = {
      inputsValidity: {},
      makeList: [],
      makeDisplayNames: [],
      modelDisplayNames: []
    };
    this.mySteps = React.createRef();
  }

  executeScroll = () => this.mySteps.current.scrollIntoView();

  componentDidUpdate() {
    const { history, isVrmValid } = this.props;
    if (isVrmValid) {
      history.push('/car-valuation/enter-user-details');
    }
  }

  fetchMakes = () => {
    axios
      .get(`${getApiUrl()}/search/stock?facets=1&pageSize=0`)
      .then(res => {
        const formattedMakeList = res.data.filterBreakdown.make.map(make => ({
          displayName: make.key,
          key: make.key
        }));

        this.setState({
          makeList: formattedMakeList,
          makeDisplayNames: formattedMakeList.map(make => make.displayName)
        });
      })
      .catch(console.warn);
  };

  fetchModels = makeKey => {
    axios
      .get(
        `${getApiUrl()}/search/stock?facets=1&standardMake=${makeKey}&pageSize=0`
      )
      .then(resp => {
        const formattedModelList = resp.data.filterBreakdown.model.map(
          model => {
            return model.key;
          }
        );
        this.setState({ modelDisplayNames: formattedModelList });
      })
      .catch(console.warn);
  };

  validateField(key, value) {
    const {
      formValues: { intent }
    } = this.props;

    if (key === 'vrm') {
      if (!value) {
        return {
          name: key,
          isValid: false,
          msg: 'Please enter your registration'
        };
      }
      return {
        name: key,
        isValid: regex.vrm.test(value) && regex.required.test(value),
        msg: 'This vehicle registration number is invalid'
      };
    } else if (key === 'mileage') {
      if (!value) {
        return {
          name: key,
          isValid: false,
          msg: "Please enter your vehicle's mileage"
        };
      }
      return {
        name: key,
        isValid: regex.numeric.test(value) && regex.required.test(value),
        msg: "We can't value a vehicle with this mileage"
      };
    } else if (key === 'intent' && !value) {
      return {
        name: key,
        isValid: false,
        msg: 'Please select an option'
      };
    } else if (
      this.mustChooseDesiredMakeAndModel(intent) &&
      ['desiredMake', 'desiredModel'].includes(key) &&
      !value
    ) {
      return {
        name: key,
        isValid: false,
        msg: `Please select a m${key.substring(8)}` // cuts off the 'desiredM' part
      };
    } else {
      return { name: key, isValid: true };
    }
  }

  checkInputsValidity() {
    const validState = {};
    Object.keys(this.props.formValues).forEach(key => {
      validState[key] = this.validateField(key, this.props.formValues[key]);
    });
    return validState;
  }

  handleChange = event => {
    const { inputChange } = this.props;
    const {
      target: { name, value }
    } = event;

    if (
      !this.state.makeList.length &&
      name === 'intent' &&
      this.mustChooseDesiredMakeAndModel(value)
    ) {
      this.fetchMakes();
    } else if (name === 'desiredMake') {
      const makeKey = this.state.makeList.find(
        make => make.displayName === value
      ).key;
      inputChange('model', '');
      this.fetchModels(makeKey);
      sendSelectMakeEvent(value);
    } else if (name === 'desiredModel') {
      sendSelectModelEvent(value);
    }

    inputChange(name, value);

    // remove red label and input border when input changes
    this.setState(prevState => {
      const updatedInputsValidity = {
        ...prevState.inputsValidity,
        [name]: undefined
      };
      return {
        inputsValidity: updatedInputsValidity
      };
    });
  };

  mustChooseDesiredMakeAndModel = intent =>
    [
      intentOptions.SELLING_BUYING_NEW,
      intentOptions.SELLING_BUYING_USED
    ].includes(intent);

  handleSubmit = event => {
    event.preventDefault();

    const { fetchCar, setOemDetails, formValues } = this.props;
    const inputsValidity = this.checkInputsValidity();
    const isFormValid = Object.keys(inputsValidity).every(
      field => inputsValidity[field].isValid
    );

    if (isFormValid) {
      fetchCar();
      this.setState({
        inputsValidity: {}
      });

      if (this.mustChooseDesiredMakeAndModel(formValues.intent)) {
        const oemDetails = this.state.makeList.find(
          item => item.displayName === formValues.desiredMake
        );
        oemDetails.desiredMake = formValues.desiredMake;
        oemDetails.desiredModel = formValues.desiredModel;
        setOemDetails(oemDetails);
      }
    } else {
      this.setState({ inputsValidity }, () => this.executeScroll());
    }
  };

  renderRadioButtons = () =>
    this.radioButtonsList.map(({ id, label }) => (
      <Input
        key={id}
        id={id}
        label={label}
        name="intent"
        onChange={this.handleChange}
        type="radio"
        value={id}
      />
    ));

  render() {
    const {
      formValues: { mileage, vrm, intent, desiredMake, desiredModel },
      isVrmValid
    } = this.props;
    const { inputsValidity, makeDisplayNames, modelDisplayNames } = this.state;

    const currentYear = new Date().getFullYear();
    const registrationStartYear = currentYear - 20;

    return (
      <div className={style.row}>
        <div className={classNames(style.col, style.formContainer)}>
          <h2 className={style.stepHeading}>
            Find out how much your car is worth
          </h2>
          <div className={style.stepImage}>
            <div className={style.greyLine} />
            <div className={style.steps} ref={this.mySteps}>
              <div data-cy="step1" className={style.stepActive}>
                1
              </div>
              <div>2</div>
              <div>3</div>
            </div>
          </div>
          <form onSubmit={this.handleSubmit}>
            <Input
              autoFocus={true}
              errorMessage={
                (inputsValidity.vrm && inputsValidity.vrm.msg) ||
                'We are unable to value this vehicle.'
              }
              isInvalid={
                (inputsValidity.vrm && !inputsValidity.vrm.isValid) ||
                (isVrmValid !== null && !isVrmValid)
              }
              label={<span>Your registration</span>}
              caption={`Remember that we can only value cars that were registered since ${registrationStartYear}`}
              name="vrm"
              onChange={this.handleChange}
              placeholder="Enter car registration"
              value={vrm}
            />
            <Input
              errorMessage={
                inputsValidity.mileage && inputsValidity.mileage.msg
              }
              isInvalid={
                inputsValidity.mileage && !inputsValidity.mileage.isValid
              }
              label={<span>Vehicle mileage</span>}
              caption="If your car has an unusually high mileage for its age we may not be able to value it."
              limit={6}
              name="mileage"
              onChange={this.handleChange}
              placeholder="Enter your vehicle's mileage"
              value={mileage}
            />
            <label>I&apos;m valuing because I&apos;m</label>
            {inputsValidity.intent ? (
              <label className={style.errorLabel}>
                {inputsValidity.intent.msg}
              </label>
            ) : null}
            <div className={style.intent}>{this.renderRadioButtons()}</div>

            {this.mustChooseDesiredMakeAndModel(intent) && (
              <div className={style.nextCarSelectorContainer}>
                <div>
                  <label>My next car will be</label>
                  <Input
                    errorMessage={
                      inputsValidity.desiredMake &&
                      inputsValidity.desiredMake.msg
                    }
                    isInvalid={
                      inputsValidity.desiredMake &&
                      !inputsValidity.desiredMake.isValid
                    }
                    name="desiredMake"
                    onChange={this.handleChange}
                    placeholder="Select make"
                    value={desiredMake}
                    type="select"
                    options={makeDisplayNames}
                  />
                </div>
                <Input
                  errorMessage={
                    inputsValidity.desiredModel &&
                    inputsValidity.desiredModel.msg
                  }
                  isInvalid={
                    inputsValidity.desiredModel &&
                    !inputsValidity.desiredModel.isValid
                  }
                  name="desiredModel"
                  onChange={this.handleChange}
                  placeholder="Select model"
                  value={desiredModel}
                  type="select"
                  options={modelDisplayNames}
                  disabled={!desiredMake}
                />
              </div>
            )}

            <Button
              data-cy="submit"
              text="Get a free valuation"
              onClick={this.handleSubmit}
            />
          </form>
          <h3>What happens next...</h3>
          <p>
            <strong>Step 2 -</strong> Tell us a few details so we can customise
            the service for you.
          </p>
          <p>
            <strong>Step 3 -</strong> Get your personalised car valuation.
          </p>
          <p className={style.partners}>
            What Car? Valuations data partners: Auto Trader
          </p>
          <h3 className={style.marginTop}>
            What factors affect a car’s value?
          </h3>
          <p>
            The most important factors that affect the value of a car are age,
            mileage and condition. Generally speaking the older the car and the
            higher the mileage, the less it will be worth.
          </p>
          <p>
            <strong>Condition</strong> is an important factor, too. Whether
            you’re selling a car privately, to a dealer or part-exchanging it,
            the more pristine the car is, the more someone is likely to pay for
            it. So depending on the value of your car, and the cost of any
            necessary repairs, it may be worth getting some of the imperfections
            fixed to ensure you get the best price.
          </p>
          <p>
            A <strong>service history</strong> can be hugely valuable,
            especially if you can show your car has been serviced by a main
            dealer using original parts. Here, the more history you have, the
            more your car can be worth, because it shows a potential purchaser
            it has been cared for.
          </p>
          <p>
            Some <strong>colours</strong> are more desirable than others. Silver
            and greys are perennial favourites, but while some buyers might be
            willing to pay more for an unusual colour, you might find that
            dealers will pay less because it could make the car harder for them
            to sell on.
          </p>
          <p>
            The amount of <strong>equipment</strong> your car has can also
            influence its value. But remember that while some buyers may dig
            deeper to get a car with a few extra bells and whistles, others
            won’t.
          </p>
          <h3 className={style.marginTop}>
            What factors can decrease a car’s value?
          </h3>
          <p>
            <strong>Condition</strong> is key, because few people want an old
            banger. Quite simply, the better your car looks and the more
            regularly it’s been maintained, the more it will be worth. There are
            some quick and easy ways of improving the value and saleability of
            your car: give it a{' '}
            <a href="https://www.whatcar.com/news/best-car-shampoos/n24633">
              wash
            </a>{' '}
            and a thorough{' '}
            <a href="https://www.whatcar.com/news/best-cordless-vacuum-cleaners/n24827">
              vacuum
            </a>
            . If the value of the car warrants it, get it serviced, and if the
            time remaining on your <strong>MOT</strong> is short, get it tested.
          </p>
          <p>
            Again, if it makes financial sense to do so, address some of the{' '}
            <strong>wear and tear items</strong> – the dents and scratches or
            worn tyres that are inevitable as a car gets older – and if you can
            get any <strong>broken parts</strong> fixed easily and cheaply, it
            can be worth doing so.
          </p>
          <p>
            Little or no <strong>service history</strong> will put some buyers
            off, so gather all the paperwork you have for the buyer to see.
          </p>
          <p>
            The number of <strong>previous owners</strong> can have a negative
            impact, too, although there’s little you can do about it, other than
            seeking comfort in the fact that you probably paid less for the car
            in the first place.
          </p>
          <p>
            <strong>Modifications</strong> can be more complex. Certain models –
            some sports cars, for example – can be more desirable and valuable
            with some aftermarket modifications. But big wheels, stickers and
            bodykits can reduce the value of some other types of models, so you
            may want to consider removing these extras before sale.
          </p>
        </div>

        <FAQs FaqContentCol1={faqContentCol1} />

        <h2>Expert advice on valuing and selling your car</h2>
        <p>
          What Car?’s team of experts have produced a wealth of information
          helping you to value and sell your car. For further reading, we
          suggest:
        </p>
        <ul className={style.adviceList}>
          <li>
            <a href="https://www.whatcar.com/advice/selling/how-much-is-your-car-worth/n1165">
              How much is your car worth
            </a>
          </li>
          <li>
            <a href="https://www.whatcar.com/advice/selling/how-to-sell-your-car/n1112">
              How to sell your car
            </a>
          </li>
          <li>
            <a href="https://www.whatcar.com/advice/selling/payment-paperwork-and-avoiding-scams/n1113">
              Payment, paperwork and avoiding scams
            </a>
          </li>
          <li>
            <a href="https://www.whatcar.com/advice/buying/how-to-negotiate-a-car-sale/n1105">
              How to negotiate a car sale
            </a>
          </li>
        </ul>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  formValues: {
    intent: state.valuation.formValues.intent,
    mileage: state.valuation.formValues.mileage,
    vrm: state.valuation.formValues.vrm,
    desiredMake: state.valuation.formValues.desiredMake,
    desiredModel: state.valuation.formValues.desiredModel
  },
  isVrmValid: state.valuation.isVrmValid
});

const mapDispatchToProps = {
  fetchCar,
  inputChange,
  setOemDetails
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EnterCarDetailsForm);



// WEBPACK FOOTER //
// ./src/components/EnterCarDetailsForm/index.js